/**
 * The root of an anubis page: the brand on the document, and how a `to` becomes a navigation.
 *
 * ```tsx
 * <Anubis brand={brand} title={brand?.title} language="es" link={RouterLink}>
 *   <Storefront />
 * </Anubis>
 * ```
 *
 * ## The brand is applied here, once
 *
 * Every public app had an effect that called its own `applyTheme(brand)` plus another that set
 * `document.title`, and checkout's lived inside a data hook. They are one effect now: whenever the
 * brand object changes — the cached copy on the first frame, the revalidated answer a moment later —
 * the variables, the faces and the tab icon are rewritten. Idempotent, so the second call is free when
 * nothing changed.
 *
 * ## Why anubis does not import a router
 *
 * Three of the four apps use react-router and one has no router at all. A component that navigated
 * with react-router's `Link` would force the dependency on `checkout`, and one that only knew `<a>`
 * would make every in-shop click a full page load. So a `to` is rendered through whatever component
 * the app hands in as `link` — react-router's `Link` satisfies the shape as it stands — and an app
 * with no router simply never passes a `to`. An `href` is always a plain anchor: another origin, a
 * `mailto:`, a legal page.
 */
import { useEffect } from 'react';
import type { ComponentType, ReactNode } from 'react';

import { applyAnubisBrand } from '@martincastroalvarez/anubis-theme/brand';
import type { AnubisBrand, AppIdentity } from '@martincastroalvarez/anubis-theme/brand';

import { Notices } from './notices';

/** What anubis passes to the app's link component. react-router's `Link` accepts all of it. */
export interface LinkComponentProps {
  to: string;
  className?: string | undefined;
  children?: ReactNode | undefined;
  onClick?: (() => void) | undefined;
  'aria-label'?: string | undefined;
  'aria-hidden'?: boolean | undefined;
  tabIndex?: number | undefined;
}

export type LinkComponent = ComponentType<LinkComponentProps>;

/**
 * The page's link component — a module value since 2026-10-21, not a context: a page has one router,
 * so `<Anubis link>` records it here as it renders (before any child reads it), and a page with no
 * router leaves it `null`.
 */
let linkComponent: LinkComponent | null = null;

/** Record the page's link component — what `<Anubis link>` does; call it directly outside one. */
export function setLinkComponent(component: LinkComponent | null | undefined): void {
  linkComponent = component ?? null;
}

/** The app's link component, or `null` when it has no router. */
export function useLinkComponent(): LinkComponent | null {
  return linkComponent;
}

/**
 * The page's language — a module value like the link component (2026-10-25): `<Anubis language>`
 * records it as it renders, so `Money` and `DateText` write `12,50 €` and `4 oct 2026` on a Spanish
 * page without every caller passing it. `undefined` is the browser's language.
 */
let pageLanguage: string | undefined;

/** Record the page's language — what `<Anubis language>` does; call it directly outside one. */
export function setPageLanguage(language: string | null | undefined): void {
  pageLanguage = language ?? undefined;
}

/** The page's language as `<Anubis>` was given it, or `undefined` (the browser's). */
export function usePageLanguage(): string | undefined {
  return pageLanguage;
}

export interface AnubisProps {
  /** The tenant's brand: the cached copy first, then the answer. `null` paints the neutral fallback. */
  brand: AnubisBrand | null | undefined;
  /** The document title. Empty keeps whatever `index.html` set. */
  title?: string | null | undefined;
  /** `<html lang>`. */
  language?: string | null | undefined;
  /** The app's router link, for every `to` on the page. */
  link?: LinkComponent | undefined;
  /**
   * An app that is a product of its own (2026-10-18, `wall`): its icons CDN folder — given, or found
   * from `apex` and `name`/`slug`/`version` like isis does — becomes the tab icon, the
   * touch icon and the manifest. Without it, the brand's mark is all three (the default).
   */
  app?: AppIdentity | undefined;
  children?: ReactNode | undefined;
}

export function Anubis({ brand, title, language, link, app, children }: AnubisProps) {
  setLinkComponent(link);
  setPageLanguage(language);
  const appName = app?.name;
  const appSlug = app?.slug;
  const appVersion = app?.version;
  const appIcons = app?.icons;
  const appApex = app?.apex;
  useEffect(() => {
    applyAnubisBrand(
      brand,
      appName
        ? { name: appName, slug: appSlug, version: appVersion, apex: appApex, icons: appIcons }
        : undefined,
    );
  }, [brand, appName, appSlug, appVersion, appApex, appIcons]);

  useEffect(() => {
    if (title) document.title = title;
  }, [title]);

  useEffect(() => {
    if (language) document.documentElement.lang = language;
  }, [language]);

  return (
    <>
      {children}
      {/* The fleet's notices (ra's store), in the brand — what a connector says when a request fails. */}
      <Notices />
    </>
  );
}
